import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { supabase } from "../config/supabaseClient";
import { startLoading, stopLoading } from "./commonSlice";
import { archiveRecords } from "../constant/utils/commonUtils";
import { statuses, EAP_REGIONS } from "../constant/constants";
import { preloadStaticDataSources, getStaticDataSourceSync } from "../constant/staticDataSources";
import { getDisabilityConfig } from "../constant/utils/disabilityStatusUtils";

/** Enrich SETA fields that have staticDataSourceName (e.g. province) with dropdown allowedValues from static data */
function enrichSetaFieldsWithStaticData(fields) {
  if (!Array.isArray(fields)) return fields;
  const PROVINCE_FALLBACK = EAP_REGIONS.filter((r) => !r.isNational).map((r) => r.name);
  return fields.map((field) => {
    if (!field?.staticDataSourceName) return field;
    let allowedValues = field.allowedValues || [];
    const staticValues = getStaticDataSourceSync(field.staticDataSourceName);
    allowedValues = staticValues?.length > 0 ? staticValues : (field.allowedValues || []);
    if (
      allowedValues.length === 0 &&
      String(field.staticDataSourceName).toLowerCase().trim() === "province"
    ) {
      allowedValues = PROVINCE_FALLBACK;
    }
    return { ...field, allowedValues };
  });
}

// Upload SETA Template
export const uploadSetaTemplate = createAsyncThunk(
  "setaTemplate/upload",
  async (data) => {
    const templateData = {
      ...data,
      status: statuses.ACTIVE,
    };
    
    const { data: responseData, error } = await supabase
      .from("seta_template")
      .insert([templateData])
      .select();
    if (error) throw error;
    return responseData[0];
  }
);

// Fetch All SETA Templates (excluding archived)
export const fetchAllSetaTemplates = createAsyncThunk(
  "setaTemplate/fetchAll",
  async (status = null) => {
    // Helper function to check if file_structure is empty
    // const isEmptyFileStructure = (fileStructure) => {
    //   if (!fileStructure) return true;
    //   if (typeof fileStructure === 'string') {
    //     try {
    //       const parsed = JSON.parse(fileStructure);
    //       return Object.keys(parsed).length === 0;
    //     } catch {
    //       return fileStructure.trim() === '';
    //     }
    //   }
    //   if (typeof fileStructure === 'object') {
    //     return Object.keys(fileStructure).length === 0;
    //   }
    //   return true;
    // };

    let query = supabase
      .from("seta_template")
      .select("id, seta_name, file_structure, status")
      .neq("status", statuses.ARCHIVED)
      // .not("file_structure", "is", null);

    // If specific status is provided, filter by it
    if (status) {
      query = query.eq("status", status);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    // Filter out templates with empty file_structure using the helper function
    // const filteredData = data.filter(template => !isEmptyFileStructure(template.file_structure));
    
    return data;
  }
);

// Fetch All SETA Templates (including all statuses)
export const fetchAllSetaTemplatesWithStatus = createAsyncThunk(
  "setaTemplate/fetchAllWithStatus",
  async () => {
    const { data, error } = await supabase
      .from("seta_template")
      .select("id, seta_name, file_structure, status");
    if (error) throw error;
    return data;
  }
);

// Delete/Archive a SETA Template (soft delete)
export const deleteSetaTemplate = createAsyncThunk(
  "setaTemplate/delete",
  async (id) => {
    const result = await archiveRecords(supabase, "seta_template", "id", id);
    if (!result.success) {
      throw new Error(result.error);
    }
    return id;
  }
);

// Update SETA Template Status
export const updateSetaTemplateStatus = createAsyncThunk(
  "setaTemplate/updateStatus",
  async ({ id, status }) => {
    const { data, error } = await supabase
      .from("seta_template")
      .update({ status })
      .eq("id", id)
      .select();
    if (error) throw error;
    return data[0];
  }
);

export const fetchSetaTemplateById = createAsyncThunk(
  "setaTemplate/fetchById",
  async (id) => {
    const { data, error } = await supabase
      .from("seta_template")
      .select("*")
      .eq("id", id);

    if (error) throw error;

    return data;
  }
);

export const fetchSetaTemplateByCompanyId = createAsyncThunk(
  "setaTemplate/fetchByCompanyIf",
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      if (!companyId) {
        return { fields: [], employmentTypes: [], disabilityTypes: [] };
      }
      dispatch(startLoading());
      await preloadStaticDataSources();
      const { data, error } = await supabase
        .from("company")
        .select("seta_template(file_structure, status)")
        .eq("id", companyId);

      if (error && error.code !== "PGRST116") throw error;

      if (!data || !data[0]) {
        return { fields: [], employmentTypes: [], disabilityTypes: [] };
      }

      // Support both object and array relation shape from Supabase
      const setaRow = Array.isArray(data[0]?.seta_template)
        ? data[0].seta_template[0]
        : data[0]?.seta_template;
      if (!setaRow) {
        return { fields: [], employmentTypes: [], disabilityTypes: [] };
      }

      let fileStructure = setaRow?.file_structure;
      if (typeof fileStructure === "string") {
        try {
          fileStructure = JSON.parse(fileStructure);
        } catch {
          fileStructure = null;
        }
      }
      if (!fileStructure) {
        return { fields: [], employmentTypes: [], disabilityTypes: [] };
      }

      const sheets = fileStructure?.sheets || [];
      const planSheet =
        sheets.find((s) => s.fields?.some((f) => f?.config?.generatePlan)) ||
        sheets[0];
      const fields = planSheet?.fields || [];

      const empSheet =
        sheets.find(
          (s) =>
            s.sheetName === "Employee" ||
            s.fields?.some((f) => f?.config?.employeeImport)
        ) || sheets[0];
      const employeeFields = empSheet?.fields || fields;

      // employmentTypes: planSheet.employmentTypes, then empSheet.employmentTypes, then file_structure.employmentTypes, then Employment Type field's allowedValues
      let employmentTypes =
        Array.isArray(planSheet?.employmentTypes) && planSheet.employmentTypes.length > 0
          ? planSheet.employmentTypes
          : Array.isArray(empSheet?.employmentTypes) && empSheet.employmentTypes.length > 0
            ? empSheet.employmentTypes
            : Array.isArray(fileStructure?.employmentTypes)
              ? fileStructure.employmentTypes
              : [];
      if (employmentTypes.length === 0 && Array.isArray(fields)) {
        const employmentTypeField =
          fields.find((f) => f?.config?.isEmploymentTypeField) ||
          employeeFields.find((f) => f?.config?.isEmploymentTypeField);
        const allowed = employmentTypeField?.allowedValues;
        if (
          Array.isArray(allowed) &&
          allowed.every((o) => o != null && typeof o === "object" && "isUnemployedType" in o)
        ) {
          employmentTypes = allowed;
        }
      }

      const { disabilityOptions: disabilityTypes } = getDisabilityConfig({
        sheet: planSheet || empSheet,
        fileStructure,
        fields,
      });

      const enrichedFields = enrichSetaFieldsWithStaticData(fields);
      const enrichedEmployeeFields = enrichSetaFieldsWithStaticData(employeeFields);

      return {
        fields: enrichedFields,
        employeeFields: enrichedEmployeeFields,
        employmentTypes,
        disabilityTypes,
        fileStructure,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateSetaTemplate = createAsyncThunk(
  "setaTemplate/update",
  async ({ id, data }) => {
    const { data: responseData, error } = await supabase
      .from("seta_template")
      .update(data)
      .eq("id", id)
      .select();
    if (error) throw error;
    return responseData[0];
  }
);

// Check if SETA Template is referenced by companies
export const checkSetaTemplateReferences = createAsyncThunk(
  "setaTemplate/checkReferences",
  async (setaTemplateId) => {
    const { data, error } = await supabase
      .from("company")
      .select("id, trading_name, registered_name")
      .eq("seta_id", setaTemplateId)
      .neq("status", statuses.ARCHIVED);
    
    if (error) throw error;
    return data;
  }
);

const setaTemplateSlice = createSlice({
  name: "setaTemplate",
  initialState: {
    templates: [],
    status: "idle",
    error: null,
    loading: false,
    setaTemplate: [],
    employeeTemplate: [],
    fileStructure: null,
    employmentTypes: [],
    disabilityTypes: [],
    referencedCompanies: [],
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadSetaTemplate.pending, (state) => {
        state.loading = true;
        state.status = "loading";
      })
      .addCase(uploadSetaTemplate.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.templates.push(action.payload);
      })
      .addCase(uploadSetaTemplate.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchAllSetaTemplates.pending, (state) => {
        state.loading = true;
        state.status = "loading";
      })
      .addCase(fetchAllSetaTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.templates = action.payload;
      })
      .addCase(fetchAllSetaTemplates.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(fetchAllSetaTemplatesWithStatus.pending, (state) => {
        state.loading = true;
        state.status = "loading";
      })
      .addCase(fetchAllSetaTemplatesWithStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded";
        state.templates = action.payload;
      })
      .addCase(fetchAllSetaTemplatesWithStatus.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(updateSetaTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.map((template) =>
          template.id === action.payload.id ? action.payload : template
        );
        preloadStaticDataSources().catch(err => {
          console.error('Error refreshing static data sources cache:', err);
        });
      })
      .addCase(updateSetaTemplateStatus.fulfilled, (state, action) => {
        state.templates = state.templates.map((template) =>
          template.id === action.payload.id ? action.payload : template
        );
      })
      .addCase(updateSetaTemplateStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSetaTemplateStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(deleteSetaTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(
          (template) => template.id !== action.payload
        );
      })
      .addCase(deleteSetaTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSetaTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchSetaTemplateById.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSetaTemplateById.fulfilled, (state, action) => {
        state.loading = false;
        state.setaTemplate = action.payload || [];
      })
      .addCase(fetchSetaTemplateById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSetaTemplateByCompanyId.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSetaTemplateByCompanyId.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.setaTemplate = Array.isArray(payload) ? payload : (payload?.fields ?? []);
        state.employeeTemplate = payload?.employeeFields ?? state.setaTemplate;
        state.fileStructure = payload?.fileStructure ?? null;
        state.employmentTypes = payload?.employmentTypes ?? [];
        state.disabilityTypes = payload?.disabilityTypes ?? [];
      })
      .addCase(fetchSetaTemplateByCompanyId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkSetaTemplateReferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkSetaTemplateReferences.fulfilled, (state, action) => {
        state.loading = false;
        state.referencedCompanies = action.payload;
      })
      .addCase(checkSetaTemplateReferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default setaTemplateSlice.reducer;
